import React, { useEffect } from 'react';
import Button from '../../../../../components/Button';
import styles from '../../../css/inventoryItemDetail.module.css';
import useFrontShopForm from '../logic/useFrontShopForm.jsx';
import useExpiryEditor from '../logic/useExpiryEditor.jsx';
import ExpiryModal from '../presentational/ExpiryModal';
import CenteredBar from '../presentational/CenteredBar';
import FSHeaderRow from '../presentational/frontshop/FSHeaderRow.jsx';
import FSDescriptionCard from '../presentational/frontshop/FSDescriptionCard.jsx';
import FSPackCategoryCard from '../presentational/frontshop/FSPackCategoryCard.jsx';
import FSSupplierCard from '../presentational/frontshop/FSSupplierCard.jsx';

export default function FrontShopFormImpl({ showUpdate = true, plusShift = false, onSubmit, categories = [], suppliers = [], errors = {}, departmentName, scanItem }) {
  const f = useFrontShopForm();
  const exp = useExpiryEditor([]);

  const onSupplierChange = val => {
    f.setSupplier(val);
    const row = suppliers.find(s => s?.suppliername === val);
    f.setSupplierCode(row?.suppliercode || '');
  };

  useEffect(() => {
    if (!scanItem) return;
    const medication = scanItem;
    if (medication.invoiceNumber) {
      f.setInvoiceNumber(medication.invoiceNumber);
    }
    if (medication.name) {
      f.setProductName(medication.name);
    }
    if (medication.packSize) {
      f.setPackSize(medication.packSize);
    }
    if (medication.barcode) {
      f.setBarcode(medication.barcode);
    }
    if (medication.description) {
      f.setNote(medication.description);
    }
    if (medication.categoryName) {
      const cat = medication.categoryName;
      const hasCat = Array.isArray(categories) && categories.includes(cat);
      if (hasCat) {
        f.setCategory(cat);
      } else {
        f.setCategory('Other');
        f.setCustomCategory(cat);
      }
    }
    if (medication.reorderLevel != null) {
      f.setReorderLevel(medication.reorderLevel);
    }
    if (medication.costPrice != null) {
      f.setCostPrice(String(medication.costPrice));
    }
    if (medication.supplierName) {
      f.setSupplier(medication.supplierName);
    }
    if (medication.supplierCode) {
      f.setSupplierCode(medication.supplierCode);
    }
  }, [scanItem, categories]);

  const payload = {
    type: 'Item',
    productType: 'Item',
    name: f.productName,
    packsize: String(f.packSize),
    shelve: f.shelve,
    reorderlevel: Number(f.reorderLevel),
    quantity: Number(f.quantity),
    costprice: f.parsedCost,
    barcode: f.barcode,
    supplier: f.supplier,
    suppliercode: f.supplierCode,
    invoicenumber: f.invoiceNumber,
    expirydates: exp.expiryList.filter(Boolean),
    category: f.category === 'Other' ? f.customCategory : f.category,
    note: f.note,
  };

  const isFormComplete = () => {
    const hasName = !!f.productName;
    const hasBarcode = !!f.barcode;
    const hasPackSize = !!f.packSize;
    const hasCategory = f.category === 'Other' ? !!f.customCategory : !!f.category;
    const hasReorder = Number(f.reorderLevel) >= 0;
    const hasQuantity = Number(f.quantity) > 0;
    const hasCost = Number(f.parsedCost) > 0;
    const hasSupplier = !!f.supplier && !!f.supplierCode;
    const hasExpiry = Array.isArray(exp.expiryList) && exp.expiryList.filter(Boolean).length > 0;

    return (
      hasName &&
      hasBarcode &&
      hasPackSize &&
      hasCategory &&
      hasReorder &&
      hasQuantity &&
      hasCost &&
      hasSupplier &&
      hasExpiry
    );
  };

  return (
    <>
      <FSHeaderRow
        f={f}
        errors={errors}
        categories={categories}
        departmentName={departmentName}
        onItemSelect={(medication, { f, categories }) => {
          if (medication?.invoiceNumber) {
            f.setInvoiceNumber(medication.invoiceNumber);
          }
          if (medication?.name) {
            f.setProductName(medication.name);
          }
          if (medication?.packSize) {
            f.setPackSize(medication.packSize);
          }
          if (medication?.barcode) {
            f.setBarcode(medication.barcode);
          }
          if (medication?.description) {
            f.setNote(medication.description);
          }
          if (medication?.categoryName) {
            const cat = medication.categoryName;
            const hasCat = Array.isArray(categories) && categories.includes(cat);
            if (hasCat) {
              f.setCategory(cat);
            } else {
              f.setCategory('Other');
              f.setCustomCategory(cat);
            }
          }
          if (medication?.reorderLevel != null) {
            f.setReorderLevel(medication.reorderLevel);
          }
          if (medication?.costPrice != null) {
            f.setCostPrice(String(medication.costPrice));
          }
          if (medication?.supplierName) {
            f.setSupplier(medication.supplierName);
          }
          if (medication?.supplierCode) {
            f.setSupplierCode(medication.supplierCode);
          }
        }}
        onBarcodeSelect={(medication, { f, categories }) => {
          if (medication?.invoiceNumber) {
            f.setInvoiceNumber(medication.invoiceNumber);
          }
          if (medication?.name) {
            f.setProductName(medication.name);
          }
          if (medication?.packSize) {
            f.setPackSize(medication.packSize);
          }
          if (medication?.barcode) {
            f.setBarcode(medication.barcode);
          }
          if (medication?.description) {
            f.setNote(medication.description);
          }
          if (medication?.categoryName) {
            const cat = medication.categoryName;
            const hasCat = Array.isArray(categories) && categories.includes(cat);
            if (hasCat) {
              f.setCategory(cat);
            } else {
              f.setCategory('Other');
              f.setCustomCategory(cat);
            }
          }
          if (medication?.reorderLevel != null) {
            f.setReorderLevel(medication.reorderLevel);
          }
          if (medication?.costPrice != null) {
            f.setCostPrice(String(medication.costPrice));
          }
          if (medication?.supplierName) {
            f.setSupplier(medication.supplierName);
          }
          if (medication?.supplierCode) {
            f.setSupplierCode(medication.supplierCode);
          }
        }}
      />

      <FSDescriptionCard f={f} errors={errors} />

      <FSPackCategoryCard f={f} exp={exp} errors={errors} categories={categories} plusShift={plusShift} />

      <FSSupplierCard f={f} errors={errors} suppliers={suppliers} onSupplierChange={onSupplierChange} />

      {exp.showExpiryModal && (
        <ExpiryModal styles={styles} list={exp.expiryList} today={exp.todayStr} setCount={exp.setCount} onChange={exp.updateExpiryAt} onRemove={exp.removeExpiryAt} onClose={() => exp.setShowExpiryModal(false)} />
      )}

      {showUpdate && (
        <CenteredBar>
          <Button
            onClick={() => {
              return Promise.resolve(onSubmit?.(payload)).then(success => {
                if (success) {
                  f.resetForm();
                }
              });
            }}
          >
            Save
          </Button>
        </CenteredBar>
      )}
    </>
  );
}
